import { type LearningCourse, type PurchaseType } from '@sr/types';
import { type EVENT_ECOM_PRODUCT } from './base';

export type LEARNING_PRICING_EVENT_PAYLOAD = {
  user_id: string | null;
  course_name: string;
  course_id: number;
  source: 'course_catalog' | 'course_page' | 'unknown';
};

export type LEARNING_PURCHASE_EVENT_CONSTANT =
  'learning_checkout_started' | 'learning_checkout_next_clicked' | 'learning_order_confirmed';

export type LEARNING_PURCHASE_EVENT_PAYLOAD =
  | LEARNING_CHECKOUT_STARTED
  | LEARNING_CHECKOUT_SELECTED_LICENSES_PAYLOAD
  | LEARNING_CHECKOUT_SELECTED_LOCATION_COST_CENTER_PAYLOAD
  | LEARNING_ORDER_CONFIRMED_PAYLOAD;

type LEARNING_CHECKOUT_BASE_PAYLOAD = {
  user_id: string | null;
  course_name: string;
  course_id: number;
  single_quantity: number;
  bundle_quantity: number;
  bundle_size: number;
  purchase_id?: string;
};

type LEARNING_CHECKOUT_STARTED = LEARNING_CHECKOUT_BASE_PAYLOAD;

type LEARNING_CHECKOUT_SELECTED_LICENSES_PAYLOAD = LEARNING_CHECKOUT_BASE_PAYLOAD & {
  step_from: 'select_licenses';
};

type LEARNING_CHECKOUT_SELECTED_LOCATION_COST_CENTER_PAYLOAD = Omit<
  LEARNING_CHECKOUT_SELECTED_LICENSES_PAYLOAD,
  'step_from'
> & {
  step_from: 'location_cost_center';
  selected_location: string;
  selected_cost_center: string;
};

type LEARNING_ORDER_CONFIRMED_PAYLOAD = Omit<
  LEARNING_CHECKOUT_SELECTED_LOCATION_COST_CENTER_PAYLOAD,
  'step_from'
> & {
  final_order_value: number;
  purchase_type: PurchaseType;
};

export const learningProducts: Record<string, EVENT_ECOM_PRODUCT> = {
  fromWasteToResourceSingleSE: {
    item_id: 'fromWasteToResource',
    item_name: 'From Waste to Resource (SE)',
    price: 1490,
    currency: 'SEK',
    quantity: 1,
  },
  fromWasteToResourceBundleSE: {
    item_id: 'fromWasteToResourceBundle',
    item_name: 'From Waste to Resource Bundle (SE)',
    price: 5990,
    currency: 'SEK',
    quantity: 1,
  },
  adr13SingleSE: {
    item_id: 'adr13',
    item_name: 'ADR 1.3 (SE)',
    price: 1500,
    currency: 'SEK',
    quantity: 1,
  },
  adr13BundleSE: {
    item_id: 'adr13Bundle',
    item_name: 'ADR 1.3 Bundle (SE)',
    price: 6750,
    currency: 'SEK',
    quantity: 1,
  },
} as const satisfies Record<string, EVENT_ECOM_PRODUCT>;

export type LearningProductKey = keyof typeof learningProducts;

export const resolveLearningProducts = (
  course: LearningCourse,
  singles: number,
  bundles: number,
): EVENT_ECOM_PRODUCT[] => {
  const resolve = (singleKey: LearningProductKey, bundleKey: LearningProductKey) => {
    const products: EVENT_ECOM_PRODUCT[] = [];

    if (bundles > 0) products.push({ ...learningProducts[bundleKey], quantity: bundles });
    if (singles > 0) products.push({ ...learningProducts[singleKey], quantity: singles });

    return products;
  };

  // OBS: The product keys are currently hardcoded based on course ID and bundle size, but should be made more dynamic in the future
  switch (course.id) {
    case 1:
      return resolve('fromWasteToResourceSingleSE', 'fromWasteToResourceBundleSE');
    case 2:
      return resolve('adr13SingleSE', 'adr13BundleSE');
    default:
      return [];
  }
};
